import { useDispatch, useSelector } from 'react-redux'
import { selectOptionToStrikeMap, selectUnderlyingOptionsExpiriesV2 } from '../../../../selectors/options'
import React, { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { getSecuritiesFromSecuritiesObj, getSecurityAsync, getSecurityKey } from '../../../../util/security'
import useSetPrimarySecurity from '../../../../hooks/useSetPrimarySecurity'
import { DropDownExpiries } from '../../../../reducer/options.reducer'
import AnalyticsManager from '../../../../analytics/AnalyticsManager'
import { ChartContext } from '../../../../components/chartV2/chart.context'
import { ChartViewKey } from '../../../../assets/constants'
import { generatePath, useNavigate } from 'react-router-dom'
import ROUTES from '../../../../assets/routes'
import { selectActiveLayoutId } from '../../../../selectors/home'
import { selectActiveWidget } from '../../../../selectors/widgetDashboard'
import { WidgetTypes } from '../../../../components/widgetDashboard/widgetDashboard.enums'
import { updateAtmOptionChart } from '../../../../reducer/chart.reducer'
import { selectActiveChartId, selectAtmOptionConfigChartSecurityKey } from '../../../../selectors/chart'
import { selectPrimarySecurity } from '../../../../selectors/home'
import { selectIsEmbedded } from '../../../../selectors/device.ts'
import { callNative } from '../../../../util/nativeBridge'
import { ExtraSmallButton, NonInteractiveTooltip } from '../../../../components/custom'
import OptionsExpiryMenuBody from './optionsExpiryMenuBody'

export type Props = {
    defaultName?: string
    chartSecurityKey?: string
}
const OptionsChartExpiryMenu: React.FC<Props> = ({ defaultName, chartSecurityKey }: Props) => {
    const dispatch = useDispatch()
    const setPrimarySecurity = useSetPrimarySecurity()
    const navigate = useNavigate()
    const chartContext = useContext(ChartContext)
    const activeChartId = chartContext?.viewKey
    const chartSecurities = chartContext?.chart?.securities
    const activeLayoutId = useSelector(selectActiveLayoutId)
    const activeWidget = useSelector(selectActiveWidget(activeLayoutId))
    const underlyingTokenFromChart = chartSecurities?.length && chartSecurities[0].exchange != null && chartSecurities[0].underlyingSecurityCode != null
        ? getSecurityKey(chartSecurities[0].exchange, chartSecurities[0].underlyingSecurityCode)
        : ''
    const underlyingTokenFromWidget = (activeWidget?.settings as { underlyingKey?: string })?.underlyingKey ?? ''
    const underlyingToken = underlyingTokenFromChart || underlyingTokenFromWidget
    const { key: primarySecurityKey } = useSelector(selectPrimarySecurity)
    const atmOptionSecurityKey = useSelector(selectAtmOptionConfigChartSecurityKey(activeChartId ?? ''))
    const contextChartKey = chartSecurities?.length
        ? getSecurityKey(chartSecurities[0].exchange, chartSecurities[0].code)
        : ''
    const key = atmOptionSecurityKey || chartSecurityKey || contextChartKey || primarySecurityKey
    const [selectedChartSecurity, setSelectedChartSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    useEffect(() => { if (key) getSecurityAsync(key).then(setSelectedChartSecurity); else setSelectedChartSecurity(undefined) }, [key])
    const optionToStrikeMap = useSelector(selectOptionToStrikeMap(underlyingToken))
    const viewKey = useSelector(selectActiveChartId)
    const isEmbedded = useSelector(selectIsEmbedded)

    const expiryLabel = useMemo(() => {
        if (!selectedChartSecurity?.expiry?.name) return ''
        return selectedChartSecurity?.expiry?.name
    }, [selectedChartSecurity])

    const handleOptionExpiryChange = useCallback(
        async (optionExpiry: DropDownExpiries, atmStrike: number) => {
            const chartSecKey = key
            const selectedOptionType = optionToStrikeMap[chartSecKey]
            let strike = selectedOptionType ? selectedOptionType.strike : 0
            let options = optionExpiry.strikes.find((s) => s.strike === strike)
            if (optionExpiry && !options) {
                strike = atmStrike ? atmStrike : optionExpiry.strikes[Math.round((optionExpiry.strikes.length - 1) / 2)].strike
                options = optionExpiry.strikes.find((s) => s.strike === strike)
            }
            const option = selectedOptionType && selectedOptionType.optionType === 'PE' ? options?.putSecurity : options?.callSecurity
            if (option) {
                const newOptionKey = getSecurityKey(option.exchange, option.code)
                const sec = await getSecuritiesFromSecuritiesObj([option])
                if (sec) {
                    setSelectedChartSecurity(sec[0])
                    if (activeChartId === ChartViewKey.SYMBOL) {
                        navigate(generatePath(ROUTES.symbol, { symbol: getSecurityKey(sec[0].exchange, sec[0].code) }))
                    }
                    if (activeWidget?.widgetType === WidgetTypes.ATM_OPTION_CHART) {
                        const configChartKey = selectedChartSecurity ? getSecurityKey(selectedChartSecurity.exchange, selectedChartSecurity.code) : chartSecKey
                        dispatch(updateAtmOptionChart({ viewKey, config: { key: configChartKey, securityKey: newOptionKey }, createIfAbsent: true }))
                    } else {
                        setPrimarySecurity(newOptionKey, sec, false)
                    }
                }
                AnalyticsManager.logEvent('Options Price Charts Expiry Changed', { selected_value: sec?.[0].code })
            }
        },
        [optionToStrikeMap, key, setPrimarySecurity, activeChartId, activeWidget, selectedChartSecurity, viewKey, dispatch, navigate]
    )

    const handleEmbeddedClick = useCallback(() => {
        if (window.isEmbeddedMode) {
            const expiry = selectedChartSecurity?.expiry?.expiry || 0
            const securityKey = key || ''
            callNative('onScalperSeriesMetaDataClicked', JSON.stringify({
                type: 'expiry',
                expiry,
                securityKey,
                timestamp: Date.now()
            }))
        } else {
            callNative('onSeriesMetaDataOptionExpiryClicked')
        }
    }, [selectedChartSecurity, key])

    if (isEmbedded) {
        return (
            <NonInteractiveTooltip placement="top" title="Change Expiry" arrow>
                <ExtraSmallButton
                    size="small"
                    variant="text"
                    onClick={handleEmbeddedClick}
                    color="inherit"
                    sx={{ padding: 0, margin: 0, display: 'inline-block', maxHeight: '24px', fontSize: '14px', minWidth: 0 }}
                >
                    {defaultName && !selectedChartSecurity?.derived ? `${expiryLabel}(${defaultName})` : expiryLabel}
                </ExtraSmallButton>
            </NonInteractiveTooltip>
        )
    }

    const hidePastExpiry = activeWidget?.widgetType === WidgetTypes.ATM_OPTION_CHART
    const hideDerivedInMenu = activeWidget?.widgetType === WidgetTypes.ATM_OPTION_CHART
    const buttonSuffix = defaultName && !selectedChartSecurity?.derived ? defaultName : undefined

    return (
        <OptionsExpiryMenuBody
            underlyingToken={underlyingToken}
            expiryLabel={expiryLabel}
            onSelect={handleOptionExpiryChange}
            hidePastExpiry={hidePastExpiry}
            hideDerivedInMenu={hideDerivedInMenu}
            buttonSuffix={buttonSuffix}
        />
    )
}

export default memo(OptionsChartExpiryMenu)
